export function capitalizeEachWord(input) {
  if (!input) return "";
  return input
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

const AU_STATES = ["VIC", "NSW", "QLD", "SA", "WA", "TAS", "NT", "ACT"];

/**
 * Pull suburb / state / postcode out of a comma-formatted address string, as a
 * fallback when an autocomplete suggestion doesn't carry structured parts (e.g.
 * the GNAF source returns only a display string). Reads the last comma segment,
 * where "Northcote VIC 3070" splits into suburb / state / postcode.
 */
export function parseAddressParts(str) {
  if (!str) return { suburb: "", state: "", postcode: "" };
  const parts = String(str).split(",").map((s) => s.trim()).filter(Boolean);
  const last = parts[parts.length - 1] || "";
  let postcode = "";
  let state = "";
  const suburbTokens = [];
  last.split(/\s+/).forEach((t) => {
    if (/^\d{4}$/.test(t)) postcode = t;
    else if (AU_STATES.includes(t.toUpperCase())) state = t.toUpperCase();
    else suburbTokens.push(t);
  });
  return { suburb: capitalizeEachWord(suburbTokens.join(" ")), state, postcode };
}

export const normalizeTime = (timeStr) => {
  const parts = timeStr.split(":");
  if (parts.length === 2) {
    return timeStr + ":00"; // add seconds if missing
  } else if (parts.length === 3) {
    return timeStr; // already has seconds
  } else {
    return ""; // or throw error if needed
  }
};

export const cleanLeadMessageText = (msg) => {
  if (!msg) return "No comments";

  // Check if it's an offer submission - if so, we might want to return it raw first so the other helper can handle it
  // But if we just want the "Message" part, this is fine.
  
  let cleaned = msg.trim()
    .replace(/^zz:\s*/i, "")
    .replace(/^"|"$/g, "")
    .replace(/^[A-Z]{1,4}:\s*\d{1,2}\/\d{1,2}\b\s*/i, "")
    .replace(/\[Buyer Portal Enquiry - [^\]]+\]\s*/g, "")
    .replace(/\[Buyer Portal Offer Submission\]\s*/g, "")
    // Strip the "You have a New lead for..." system message
    .replace(/You have a New lead for [^.]+\. You can view the lead details in the CRM or at the following link: https?:\/\/[^\s]+\s*/gi, "")
    // Strip legacy appended notes with timestamp pattern like [30/3/2026 2:59 pm] hello
    .replace(/\s*\[\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}\s*[ap]m\]\s*[^\[]*(?=\[|$)/gi, "")
    .trim();

  if (cleaned.includes("Message:")) {
    const parts = cleaned.split("Message:");
    const actualMsg = parts[1]?.trim();
    if (actualMsg && actualMsg !== "No message") return actualMsg;
    return "No comments";
  }

  return cleaned || "No comments";
};

export const formatOfferSubmissionSummary = (msg) => {
  if (!msg || !msg.includes("[Buyer Portal Offer Submission]")) return msg;

  try {
    const amountMatch = msg.match(/Offer Amount: \$([\d,]+)/);
    const depositMatch = msg.match(/Deposit: \$([\d,.]+)/);
    const settlementMatch = msg.match(/Settlement: (\d+) days/);
    const conditionsMatch = msg.match(/Conditions: (\[.*\])/);

    const amount = amountMatch ? amountMatch[1] : "0";
    const deposit = depositMatch ? depositMatch[1].replace(/\.00$/, "") : "0";
    const settlement = settlementMatch ? settlementMatch[1] : "0";

    let conditionsStr = "";
    if (conditionsMatch) {
      try {
        const rawConditions = conditionsMatch[1].replace(/'/g, '"');
        const parsedConditions = JSON.parse(rawConditions);
        conditionsStr = parsedConditions
          .map(c => c.type.replace(/_/g, " ").replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()))
          .join(", ");
      } catch (e) {
        const types = [...conditionsMatch[1].matchAll(/'type':\s*'([^']+)'/g)].map(m =>
          m[1].replace(/_/g, " ").replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
        );
        conditionsStr = types.join(", ");
      }
    }

    let result = `Buyer Portal Offer Submission - Offer amount: $${amount} Deposit: $${deposit} Settlement Conditions: ${settlement} days`;
    if (conditionsStr) {
      result += ` Conditions: ${conditionsStr}.`;
    } else {
      result += ` Conditions: Subject to Finance.`; // Fallback to user's example if empty or just "None"
    }

    return result;
  } catch (err) {
    return msg;
  }
};


/** Turn axios/DRF error payloads into a short user-facing message. */
export function formatApiError(error, fallback = 'Something went wrong. Please try again.') {
  const data = error?.response?.data;
  if (!data) {
    if (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error') {
      return 'Unable to reach the server. Check your connection and try again.';
    }
    return error?.message || fallback;
  }
  if (typeof data === 'string') return data;
  if (data.message) return data.message;
  if (data.detail) return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
  if (data.errors && typeof data.errors === 'object') {
    const parts = Object.entries(data.errors).flatMap(([field, msgs]) => {
      const list = Array.isArray(msgs) ? msgs : [msgs];
      return list.map((m) => `${field.replace(/_/g, ' ')}: ${m}`);
    });
    if (parts.length) return parts.join('. ');
  }
  const fieldErrors = Object.entries(data)
    .filter(([, v]) => Array.isArray(v) || typeof v === 'string')
    .flatMap(([field, msgs]) => {
      const list = Array.isArray(msgs) ? msgs : [msgs];
      return list.map((m) => `${field.replace(/_/g, ' ')}: ${m}`);
    });
  if (fieldErrors.length) return fieldErrors.join('. ');
  return fallback;
}

/**
 * Clean an email typed or pasted into a form before we send it to the API.
 * Handles the common "copy sender from Outlook/Gmail" case where the field
 * ends up holding `Raid Misconi <raid@yahoo.com>` (which the backend rejects
 * as invalid), plus stray whitespace and invisible characters (zero-width
 * space, non-breaking space) that a paste can smuggle in. DOMA 13121356668.
 */
export function normalizeEmail(value) {
  if (!value) return '';
  let email = String(value)
    // Strip zero-width and non-breaking spaces a paste can leave behind.
    .replace(/[\u200B-\u200D\uFEFF\u00A0]/g, '')
    .trim();
  // Unwrap the display-name form: `Name <addr>` -> `addr`.
  const angled = email.match(/<([^<>]+)>/);
  if (angled) email = angled[1].trim();
  return email;
}

export function formatLeadSummaryFrequency(hours) {
  if (hours === 168) return "Weekly";
  if (hours === 24) return "24 hours";
  if (typeof hours === "number") return `${hours} hours`;
  // fallback for string input
  if (typeof hours === "string") {
    const n = parseInt(hours, 10);
    if (n === 168) return "Weekly";
    if (n === 24) return "24 hours";
    if (!isNaN(n)) return `${n} hours`;
  }
  return "24 hours";
}